import moment from "moment";
import React from "react";
import { Link } from "react-router";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { Carousel } from "@domain/carousel/carousel";
import { useIsPerson } from "@domain/currentProfile/hooks";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useUserProfile } from "@domain/user/hooks";
import { DetailedPerson } from "@domain/user/user";
import { useIsMobile } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { mobile } from "@ui/styles/utils";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { CalendarIconBis2, EyeIcon, RepostIcon } from "@ui/components/icons";
import { Bloc, Stack } from "@ui/components/layout";
import { FollowButton } from "@ui/components/profile/followButton";
import { UserView } from "@ui/components/profile/userView";

interface CarouselInfosViewProps extends React.HTMLAttributes<HTMLDivElement> {
	carousel: Carousel;
}

export const CarouselInfosView: React.FC<CarouselInfosViewProps> = ({ carousel, ...htmlProps }) => {
	const displayedAuthor = getDisplayedAuthor(carousel);
	const isAuthor = useIsPerson(carousel?.author ?? {});
	const authorId = carousel.author?.id;
	const isMobile = useIsMobile();
	const { result: profile, loading } = useUserProfile(authorId);
	if (loading) return <ClipLoader />;
	return (
		<Stack {...htmlProps} gap={10} justify="center">
			<CarouselTitle>{carousel?.title}</CarouselTitle>
			<Infos>
				<CategoryLabel category={carousel?.category} isTitleTruncated={isMobile} />
				<StyledInfos>
					<CalendarIconBis2 />
					{moment(new Date(carousel?.createdAt)).format("DD/MM/YY")}
				</StyledInfos>
				<StyledInfos>
					<EyeIcon />
					{carousel?.views_count}
				</StyledInfos>
				<StyledInfos>
					<RepostIcon color={theme.colors.black} />
					{carousel?.shares_count}
				</StyledInfos>
			</Infos>
			<StyledOwnerRow>
				<StyledUserView>
					<Link
						to={
							displayedAuthor.id
								? displayedAuthor.type === "USER"
									? Path.Profile(displayedAuthor)
									: Path.Organization(displayedAuthor)
								: ""
						}
					>
						<UserView
							inline
							user={displayedAuthor}
							isAuthor={isAuthor}
							showDescription={!isMobile}
							pictureSize={"L"}
						></UserView>
					</Link>
				</StyledUserView>
				{!isAuthor && (
					<StyledFollowBloc>
						<FollowButton isBig iconMode={isMobile} profile={profile as DetailedPerson} />
					</StyledFollowBloc>
				)}
			</StyledOwnerRow>
		</Stack>
	);
};

const CarouselTitle = styled.div`
	display: flex;
	flex-wrap: wrap;
	margin-bottom: 18px !important;
	margin-top: 0px !important;
	${({ theme }) => theme.textStyles.title}
`;
const Infos = styled.div`
	display: flex;
	gap: 20px;
	@media ${mobile} {
		gap: 8px;
	}
`;

const StyledInfos = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	justify-content: center;
`;

const StyledUserView = styled(Bloc)`
	cursor: pointer;
	@media ${mobile} {
		margin-bottom: 25px;
	}
`;

const StyledOwnerRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: #ffffff;
	width: 100%;
	border-radius: 7px;
`;

const StyledFollowBloc = styled(Bloc)`
	margin-right: 20px;
`;
